import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoGameControllerOutline} from "react-icons/io5";
import { SlSupport } from "react-icons/sl";
import { RiSpeakLine } from "react-icons/ri";
import { MdMediaBluetoothOn } from "react-icons/md";
import { CiLock } from "react-icons/ci";
import { BsWindowFullscreen } from "react-icons/bs";
import { usePublicLang } from "../../../config/i18n/public";

const FEATURE_ICONS = [
  IoGameControllerOutline,
  SlSupport,
  RiSpeakLine,
  MdMediaBluetoothOn,
  BsWindowFullscreen,
  CiLock,
];

export default function FeaturesSixGrid({
  /* A front frame of the current turntable, cut out for dark backgrounds. At
     8% it is a silhouette, so the small frame is plenty and costs 47kB. */
  deviceBg = "/steffi/turn-alpha/turn_000.webp",
}) {
  const { copy } = usePublicLang();
  const items = useMemo(
    () =>
      copy.features.items.map((item, i) => ({
        ...item,
        icon: FEATURE_ICONS[i] ?? null,
      })),
    [copy.features.items]
  );

  const sectionRef = useRef(null);
  const startedRef = useRef(false);
  const revealTimeoutsRef = useRef([]);
  const [visible, setVisible] = useState(() => Array(items.length).fill(false));

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || startedRef.current) return;

          startedRef.current = true;
          revealTimeoutsRef.current = items.map((_, idx) =>
            window.setTimeout(() => {
              setVisible((prev) => {
                if (prev[idx]) return prev;
                const next = [...prev];
                next[idx] = true;
                return next;
              });
            }, idx * 180)
          );
          observer.disconnect();
        });
      },
      {
        root: null,
        rootMargin: "-20% 0px -20% 0px",
        threshold: 0.05,
      }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      revealTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      revealTimeoutsRef.current = [];
    };
  }, [items]);

  return (
    <section ref={sectionRef} className="relative w-full bg-[#101011] text-white overflow-x-hidden">
      <div className="h-[1px] w-full bg-white/10" aria-hidden />

      {/* faint device image (ensure it sits below content) */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden z-0">
        <div className="sticky top-16 pointer-events-none">
          <img
            src={deviceBg}
            alt=""
            className="
              opacity-[0.08]
              ml-0 sm:-ml-6
              w-[85vw] sm:w-[520px]
              max-w-none
              select-none
            "
            draggable="false"
          />
        </div>
      </div>

      {/* content above bg */}
      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-x-16 gap-y-24 sm:grid-cols-2 md:grid-cols-3">
          {items.map((it, i) => {
            const isVisible = visible[i];
            return (
              <div
                key={it.title}
                className={`min-w-0 transition-all duration-700 ease-out will-change-transform ${
                  i >= 3 ? "md:mt-12" : ""
                } ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              >
                <FeatureTile title={it.title} body={it.body} icon={it.icon} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --- Tile --- */
function FeatureTile({ title, body, icon: Icon }) {
  const SafeIcon = Icon ?? BrainDumbbellIcon;
  return (
    <div className="relative">
      <div className="mb-4">
        <div className="grid h-24 w-24 place-items-center rounded-full bg-[#79B7FF] text-[#0b1b2d]">
          <SafeIcon className="h-10 w-10" />
        </div>
      </div>
      <h3 className="text-[22px] md:text-[24px] font-extrabold leading-[1.15] tracking-[-0.01em] whitespace-normal break-words">
        {title}
      </h3>
      <p className="mt-4 w-full max-w-full sm:max-w-[28rem] text-[16px] md:text-[17px] leading-[1.45] text-[#a39e9e] font-bold break-words">
        {body}
      </p>
    </div>
  );
}

function BrainDumbbellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 8a4 4 0 118 0v3H8V8z" opacity=".7" />
      <path d="M4 13h3v-2H4v2zm13 0h3v-2h-3v2zM8 14h8v-4H8v4z" />
    </svg>
  );
}
